import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Button, Tabs, Tab } from "react-bootstrap";
import Select from "react-select";
import { BiArrowBack } from "react-icons/bi";
import { MdDeleteOutline, MdOutlineEdit } from "react-icons/md";
import {
  getCampaign,
  updateCampaign,
  activateCampaign,
  pauseCampaign,
  resumeCampaign,
  getTargeting,
  updateTargeting,
  listAdvertisements,
  createAdvertisement,
  updateAdvertisement,
  deleteAdvertisement,
  uploadVideo,
  getAnalytics,
  exportAnalytics,
  fetchStates,
  fetchCities,
  fetchCrafts,
} from "../../Service/advertisingApi";
import { hasPermission } from "../../Utils/permissionUtils";
import { DeletePopupModal } from "../../Components/PopupModal/DeletePopupModal";
import AlertDeletePopupModal from "../../Components/PopupModal/AlertDeletePopupModal";
import {
  CAMPAIGN_STATUSES,
  STATUS_COLORS,
  GENDER_OPTIONS,
  AGE_RANGE_OPTIONS,
  PLACEMENT_OPTIONS,
  AD_TYPE_OPTIONS,
  formatCurrency,
  formatNumber,
  formatPercent,
  buildAdvertisementPayload,
  buildBannerFormData,
  formatApiError,
  getAdUuid,
  getAdUuidFromResponse,
  toDatetimeLocal,
  fromDatetimeLocal,
} from "./constants";
import "./advertising.css";

const emptyAdForm = {
  title: "",
  description: "",
  ad_type: "image",
  cta_text: "Know More",
  destination_url: "",
  placements: [],
  is_active: true,
  banner_image: null,
};

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 38, fontSize: 14 }),
  menu: (base) => ({ ...base, zIndex: 10 }),
};

const AdvertisingCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [form, setForm] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  const [targeting, setTargeting] = useState(null);
  const [targetForm, setTargetForm] = useState({
    target_genders: [],
    target_age_ranges: [],
    target_state_ids: [],
    target_city_ids: [],
    target_craft_ids: [],
  });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [crafts, setCrafts] = useState([]);
  const [targetSaving, setTargetSaving] = useState(false);

  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState(emptyAdForm);
  const [adError, setAdError] = useState("");
  const [adSaving, setAdSaving] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteAdUuid, setDeleteAdUuid] = useState(null);
  const [alertDeleteType, setAlertDeleteType] = useState("");
  const [deletePopupType, setDeletePopupType] = useState("");

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canChange = hasPermission("advertising.change_adcampaign");
  const canDelete = hasPermission("advertising.delete_adcampaign");
  const canAddAd = hasPermission("advertising.add_advertisement");
  const canChangeAd = hasPermission("advertising.change_advertisement");
  const canDeleteAd = hasPermission("advertising.delete_advertisement");

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCampaign(id);
      const c = res.data;
      setCampaign(c);
      setForm({
        name: c.name || "",
        advertiser_name: c.advertiser_name || "",
        start_date: toDatetimeLocal(c.start_date),
        end_date: toDatetimeLocal(c.end_date),
        status: c.status || "draft",
        priority: c.priority ?? 5,
        budget: c.budget ?? "",
        notes: c.notes || "",
      });
      if (c.advertisements) setAds(c.advertisements);
      if (c.targeting) setTargeting(c.targeting);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadTargeting = useCallback(async () => {
    try {
      const res = await getTargeting(id);
      const t = res.data;
      setTargeting(t);
      setTargetForm({
        target_genders: t.target_genders || [],
        target_age_ranges: t.target_age_ranges || [],
        target_state_ids: (t.target_states || []).map((s) => s.id),
        target_city_ids: (t.target_cities || []).map((c) => c.id),
        target_craft_ids: (t.target_crafts || []).map((c) => c.id),
      });
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  const loadAds = useCallback(async () => {
    setAdsLoading(true);
    try {
      const res = await listAdvertisements(id);
      setAds(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (err) {
      console.error(err);
    } finally {
      setAdsLoading(false);
    }
  }, [id]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await getAnalytics(id);
      setAnalytics(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [id]);

  const loadMasters = useCallback(async () => {
    try {
      const [statesRes, craftsRes] = await Promise.all([fetchStates(), fetchCrafts()]);
      setStates(statesRes.data.results || statesRes.data || []);
      setCrafts(craftsRes.data.results || craftsRes.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadCampaign();
    loadMasters();
  }, [loadCampaign, loadMasters]);

  useEffect(() => {
    if (activeTab === "targeting") loadTargeting();
    if (activeTab === "ads") loadAds();
    if (activeTab === "analytics") loadAnalytics();
  }, [activeTab, loadTargeting, loadAds, loadAnalytics]);

  useEffect(() => {
    const loadCitiesForStates = async () => {
      if (!targetForm.target_state_ids.length) {
        setCities([]);
        return;
      }
      try {
        const allCities = [];
        for (const stateId of targetForm.target_state_ids) {
          const res = await fetchCities(stateId);
          const list = res.data.results || res.data || [];
          allCities.push(...list);
        }
        const unique = Array.from(new Map(allCities.map((c) => [c.id, c])).values());
        setCities(unique);
      } catch (err) {
        console.error(err);
      }
    };
    loadCitiesForStates();
  }, [targetForm.target_state_ids]);

  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.id, label: s.state_name || s.name })),
    [states]
  );
  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: c.id, label: c.city_name || c.name })),
    [cities]
  );
  const craftOptions = useMemo(
    () => crafts.map((c) => ({ value: c.id, label: c.frame_name || c.name })),
    [crafts]
  );

  const handleSaveCampaign = async (e) => {
    e.preventDefault();
    if (!canChange) return;
    setSaveError("");
    setSaving(true);
    try {
      if (logoFile) {
        const payload = new FormData();
        Object.entries(form).forEach(([key, val]) => {
          if (key === "start_date" || key === "end_date") {
            const iso = fromDatetimeLocal(val);
            if (iso) payload.append(key, iso);
          } else if (val !== "" && val !== null) {
            payload.append(key, String(val));
          }
        });
        payload.append("sponsor_logo", logoFile);
        await updateCampaign(id, payload);
      } else {
        const payload = {
          name: form.name,
          advertiser_name: form.advertiser_name,
          status: form.status,
          priority: Number(form.priority),
          budget: form.budget !== "" ? Number(form.budget) : null,
          notes: form.notes,
          start_date: fromDatetimeLocal(form.start_date),
          end_date: fromDatetimeLocal(form.end_date),
        };
        await updateCampaign(id, payload);
      }
      setLogoFile(null);
      loadCampaign();
    } catch (err) {
      setSaveError(err.response?.data?.detail || "Failed to save campaign.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusAction = async (action) => {
    setActionLoading(action);
    try {
      if (action === "activate") await activateCampaign(id);
      else if (action === "pause") await pauseCampaign(id);
      else if (action === "resume") await resumeCampaign(id);
      loadCampaign();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading("");
    }
  };

  const handleSaveTargeting = async (e) => {
    e.preventDefault();
    if (!canChange) return;
    setTargetSaving(true);
    try {
      await updateTargeting(id, targetForm);
      loadTargeting();
    } catch (err) {
      console.error(err);
    } finally {
      setTargetSaving(false);
    }
  };

  const openAdModal = (ad = null) => {
    if (ad) {
      setEditingAd(ad);
      setAdForm({
        title: ad.title || "",
        description: ad.description || "",
        ad_type: ad.ad_type || "image",
        cta_text: ad.cta_text || "Know More",
        destination_url: ad.destination_url || "",
        placements: ad.placements || [],
        is_active: ad.is_active !== false,
        banner_image: null,
      });
    } else {
      setEditingAd(null);
      setAdForm(emptyAdForm);
    }
    setVideoFile(null);
    setAdError("");
    setShowAdModal(true);
  };

  const handleSaveAd = async (e) => {
    e.preventDefault();
    setAdError("");
    if (!adForm.title.trim() || !adForm.destination_url.trim()) {
      setAdError("Title and destination URL are required.");
      return;
    }
    if (!editingAd && adForm.ad_type === "image" && !(adForm.banner_image instanceof File)) {
      setAdError("Banner image is required for new image ads.");
      return;
    }

    const editUuid = editingAd ? getAdUuid(editingAd) : null;
    if (editingAd && !editUuid) {
      setAdError("Cannot edit: advertisement ID missing. Refresh the page and try again.");
      return;
    }

    const hasBannerFile = adForm.banner_image instanceof File;
    setAdSaving(true);
    try {
      let savedAd;
      let createResponse;
      let adUuid;

      if (editingAd) {
        const res = await updateAdvertisement(editUuid, buildAdvertisementPayload(adForm));
        savedAd = res.data;
        adUuid = getAdUuid(savedAd) || editUuid;
        if (hasBannerFile && adUuid) {
          const patchRes = await updateAdvertisement(adUuid, buildBannerFormData(adForm.banner_image));
          savedAd = patchRes.data;
        }
      } else {
        createResponse = await createAdvertisement(id, buildAdvertisementPayload(adForm));
        savedAd = createResponse.data;
        adUuid = getAdUuid(savedAd) || getAdUuidFromResponse(createResponse);
        if (hasBannerFile && adUuid) {
          const patchRes = await updateAdvertisement(adUuid, buildBannerFormData(adForm.banner_image));
          savedAd = patchRes.data;
        }
      }

      if (adForm.ad_type === "video" && videoFile && adUuid) {
        setUploadProgress(0);
        await uploadVideo(adUuid, videoFile, (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        });
      }

      setShowAdModal(false);
      loadAds();
      loadCampaign();
    } catch (err) {
      setAdError(formatApiError(err, "Failed to save advertisement."));
    } finally {
      setAdSaving(false);
      setUploadProgress(0);
    }
  };

  const handleUploadVideoOnly = async (ad) => {
    const adUuid = getAdUuid(ad);
    if (!videoFile || !adUuid) return;
    setAdSaving(true);
    setUploadProgress(0);
    try {
      await uploadVideo(adUuid, videoFile, (evt) => {
        if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
      });
      setVideoFile(null);
      loadAds();
    } catch (err) {
      console.error(err);
    } finally {
      setAdSaving(false);
      setUploadProgress(0);
    }
  };

  const confirmDeleteAd = async (uuid) => {
    try {
      await deleteAdvertisement(uuid);
      setAlertDeleteType("");
      setDeleteAdUuid(null);
      setDeletePopupType("success");
      loadAds();
      loadCampaign();
    } catch (err) {
      setDeletePopupType("error");
      setAlertDeleteType("");
      console.error(err);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportAnalytics(id);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `campaign-${id}-analytics.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !campaign) {
    return (
      <div className="advertising-page">
        <p className="adv-muted">Loading campaign…</p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="advertising-page">
        <p className="adv-error">Campaign not found.</p>
        <button type="button" className="adv-btn adv-btn--ghost" onClick={() => navigate("/dashboard/advertising/campaigns")}>
          Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="advertising-page">
      <div className="adv-detail-header">
        <button type="button" className="adv-back-btn" onClick={() => navigate("/dashboard/advertising/campaigns")}>
          <BiArrowBack size={20} />
        </button>
        <div className="adv-detail-title-block">
          {campaign.sponsor_logo ? (
            <img src={campaign.sponsor_logo} alt="" className="adv-detail-logo" />
          ) : null}
          <div>
            <h1 className="adv-title">{campaign.name}</h1>
            <p className="adv-subtitle">
              {campaign.advertiser_name} ·{" "}
              <span
                className="adv-status-badge"
                style={{
                  background: `${STATUS_COLORS[campaign.status] || "#6b7280"}22`,
                  color: STATUS_COLORS[campaign.status],
                }}
              >
                {campaign.status}
              </span>
            </p>
          </div>
        </div>
        <div className="adv-detail-actions">
          {canChange && campaign.status === "draft" && (
            <button
              type="button"
              className="adv-btn adv-btn--success"
              disabled={actionLoading === "activate"}
              onClick={() => handleStatusAction("activate")}
            >
              Activate
            </button>
          )}
          {canChange && campaign.status === "active" && (
            <button
              type="button"
              className="adv-btn adv-btn--warn"
              disabled={actionLoading === "pause"}
              onClick={() => handleStatusAction("pause")}
            >
              Pause
            </button>
          )}
          {canChange && campaign.status === "paused" && (
            <button
              type="button"
              className="adv-btn adv-btn--success"
              disabled={actionLoading === "resume"}
              onClick={() => handleStatusAction("resume")}
            >
              Resume
            </button>
          )}
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || "details")} className="adv-tabs">
        <Tab eventKey="details" title="Details">
          <form className="adv-panel" onSubmit={handleSaveCampaign}>
            {saveError && <div className="adv-form-error">{saveError}</div>}
            <div className="adv-form-grid">
              <label className="adv-field">
                <span>Campaign Name</span>
                <input
                  className="adv-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>Advertiser Name</span>
                <input
                  className="adv-input"
                  value={form.advertiser_name}
                  onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })}
                  disabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>Start Date</span>
                <input
                  type="datetime-local"
                  className="adv-input"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  disabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>End Date</span>
                <input
                  type="datetime-local"
                  className="adv-input"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  disabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>Status</span>
                <select
                  className="adv-select adv-select--full"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  disabled={!canChange}
                >
                  {CAMPAIGN_STATUSES.filter((s) => s.value).map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="adv-field">
                <span>Priority</span>
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="adv-input"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  disabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>Budget (INR)</span>
                <input
                  type="number"
                  min={0}
                  className="adv-input"
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  disabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>Sponsor Logo</span>
                {campaign.sponsor_logo && !logoFile ? (
                  <img src={campaign.sponsor_logo} alt="" className="adv-preview-img" />
                ) : null}
                {canChange && (
                  <input
                    type="file"
                    accept="image/*"
                    className="adv-input"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  />
                )}
              </label>
              <label className="adv-field adv-field--full">
                <span>Notes</span>
                <textarea
                  className="adv-textarea"
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  disabled={!canChange}
                />
              </label>
            </div>
            {canChange && (
              <div className="adv-form-actions">
                <button type="submit" className="adv-btn adv-btn--primary" disabled={saving}>
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            )}
          </form>
        </Tab>

        <Tab eventKey="targeting" title="Targeting">
          <form className="adv-panel" onSubmit={handleSaveTargeting}>
            <p className="adv-hint">Leave selections empty to show ads to everyone for that dimension.</p>
            <div className="adv-form-grid adv-form-grid--single">
              <label className="adv-field">
                <span>Genders</span>
                <Select
                  isMulti
                  options={GENDER_OPTIONS}
                  value={GENDER_OPTIONS.filter((o) => targetForm.target_genders.includes(o.value))}
                  onChange={(sel) =>
                    setTargetForm({ ...targetForm, target_genders: (sel || []).map((s) => s.value) })
                  }
                  styles={selectStyles}
                  isDisabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>Age Ranges</span>
                <Select
                  isMulti
                  options={AGE_RANGE_OPTIONS}
                  value={AGE_RANGE_OPTIONS.filter((o) => targetForm.target_age_ranges.includes(o.value))}
                  onChange={(sel) =>
                    setTargetForm({ ...targetForm, target_age_ranges: (sel || []).map((s) => s.value) })
                  }
                  styles={selectStyles}
                  isDisabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>States</span>
                <Select
                  isMulti
                  options={stateOptions}
                  value={stateOptions.filter((o) => targetForm.target_state_ids.includes(o.value))}
                  onChange={(sel) => {
                    const ids = (sel || []).map((s) => s.value);
                    setTargetForm({
                      ...targetForm,
                      target_state_ids: ids,
                      target_city_ids: targetForm.target_city_ids.filter((cid) =>
                        cities.some((c) => c.id === cid && ids.includes(c.state_id || c.state))
                      ),
                    });
                  }}
                  styles={selectStyles}
                  isDisabled={!canChange}
                />
              </label>
              <label className="adv-field">
                <span>Cities</span>
                <Select
                  isMulti
                  options={cityOptions}
                  value={cityOptions.filter((o) => targetForm.target_city_ids.includes(o.value))}
                  onChange={(sel) =>
                    setTargetForm({ ...targetForm, target_city_ids: (sel || []).map((s) => s.value) })
                  }
                  styles={selectStyles}
                  isDisabled={!canChange || !targetForm.target_state_ids.length}
                  placeholder={targetForm.target_state_ids.length ? "Select cities…" : "Select states first"}
                />
              </label>
              <label className="adv-field">
                <span>Crafts (24 Frames)</span>
                <Select
                  isMulti
                  options={craftOptions}
                  value={craftOptions.filter((o) => targetForm.target_craft_ids.includes(o.value))}
                  onChange={(sel) =>
                    setTargetForm({ ...targetForm, target_craft_ids: (sel || []).map((s) => s.value) })
                  }
                  styles={selectStyles}
                  isDisabled={!canChange}
                />
              </label>
            </div>
            {targeting && (
              <div className="adv-targeting-summary">
                <h4>Current targeting</h4>
                <ul>
                  <li>Genders: {targeting.target_genders?.length ? targeting.target_genders.join(", ") : "All"}</li>
                  <li>Ages: {targeting.target_age_ranges?.length ? targeting.target_age_ranges.join(", ") : "All"}</li>
                  <li>
                    States:{" "}
                    {targeting.target_states?.length
                      ? targeting.target_states.map((s) => s.state_name).join(", ")
                      : "All"}
                  </li>
                  <li>
                    Cities:{" "}
                    {targeting.target_cities?.length
                      ? targeting.target_cities.map((c) => c.city_name).join(", ")
                      : "All"}
                  </li>
                  <li>
                    Crafts:{" "}
                    {targeting.target_crafts?.length
                      ? targeting.target_crafts.map((c) => c.frame_name).join(", ")
                      : "All"}
                  </li>
                </ul>
              </div>
            )}
            {canChange && (
              <div className="adv-form-actions">
                <button type="submit" className="adv-btn adv-btn--primary" disabled={targetSaving}>
                  {targetSaving ? "Saving…" : "Save Targeting"}
                </button>
              </div>
            )}
          </form>
        </Tab>

        <Tab eventKey="ads" title={`Advertisements (${ads.length})`}>
          <div className="adv-panel">
            <div className="adv-ads-toolbar">
              {canAddAd && (
                <button type="button" className="adv-btn adv-btn--primary" onClick={() => openAdModal()}>
                  Add Advertisement
                </button>
              )}
            </div>
            {adsLoading ? (
              <p className="adv-muted">Loading advertisements…</p>
            ) : ads.length === 0 ? (
              <p className="adv-muted">No advertisements yet.</p>
            ) : (
              <div className="adv-ads-grid">
                {ads.map((ad) => (
                  <article key={getAdUuid(ad)} className="adv-ad-card">
                    <div className="adv-ad-media">
                      {ad.ad_type === "video" && ad.thumbnail ? (
                        <img src={ad.thumbnail} alt={ad.title} />
                      ) : ad.banner_image ? (
                        <img src={ad.banner_image} alt={ad.title} />
                      ) : (
                        <div className="adv-ad-placeholder">{ad.ad_type}</div>
                      )}
                      <span className={`adv-ad-type-badge ${ad.ad_type}`}>{ad.ad_type}</span>
                    </div>
                    <div className="adv-ad-body">
                      <h3>{ad.title}</h3>
                      <p className="adv-cell-sub">{ad.cta_text} → {ad.destination_url}</p>
                      {ad.placements?.length ? (
                        <div className="adv-placements">
                          {ad.placements.map((p) => (
                            <span key={p} className="adv-placement-tag">
                              {p}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="adv-muted">All placements</span>
                      )}
                      <div className="adv-ad-meta">
                        <span className={ad.is_active ? "adv-active" : "adv-inactive"}>
                          {ad.is_active ? "Active" : "Inactive"}
                        </span>
                        {ad.video_url && (
                          <a href={ad.video_url} target="_blank" rel="noopener noreferrer" className="adv-link">
                            Video
                          </a>
                        )}
                      </div>
                      <div className="adv-actions">
                        {canChangeAd && (
                          <button type="button" className="adv-btn adv-btn--ghost" onClick={() => openAdModal(ad)}>
                            <MdOutlineEdit size={16} /> Edit
                          </button>
                        )}
                        {canDeleteAd && (
                          <button
                            type="button"
                            className="adv-btn adv-btn--ghost adv-btn--danger"
                            onClick={() => {
                              setDeleteAdUuid(getAdUuid(ad));
                              setAlertDeleteType("success");
                            }}
                          >
                            <MdDeleteOutline size={16} />
                          </button>
                        )}
                      </div>
                      {canChangeAd && ad.ad_type === "video" && !ad.video_url && (
                        <div className="adv-video-upload-inline">
                          <input
                            type="file"
                            accept="video/*"
                            className="adv-input"
                            onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                          />
                          <button
                            type="button"
                            className="adv-btn adv-btn--primary"
                            disabled={!videoFile || adSaving}
                            onClick={() => handleUploadVideoOnly(ad)}
                          >
                            Upload Video
                          </button>
                          {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="adv-progress">
                              <div className="adv-progress-bar" style={{ width: `${uploadProgress}%` }} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </Tab>

        <Tab eventKey="analytics" title="Analytics">
          <div className="adv-panel">
            <div className="adv-analytics-toolbar">
              <button
                type="button"
                className="adv-btn adv-btn--ghost"
                onClick={loadAnalytics}
                disabled={analyticsLoading}
              >
                Refresh
              </button>
              <button
                type="button"
                className="adv-btn adv-btn--primary"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
            </div>
            {analyticsLoading && !analytics ? (
              <p className="adv-muted">Loading analytics…</p>
            ) : analytics ? (
              <>
                <div className="adv-metrics-grid">
                  <div className="adv-metric-card">
                    <span className="adv-metric-label">Impressions</span>
                    <span className="adv-metric-value">{formatNumber(analytics.impressions)}</span>
                  </div>
                  <div className="adv-metric-card">
                    <span className="adv-metric-label">Unique Reach</span>
                    <span className="adv-metric-value">{formatNumber(analytics.unique_reach)}</span>
                  </div>
                  <div className="adv-metric-card">
                    <span className="adv-metric-label">Clicks</span>
                    <span className="adv-metric-value">{formatNumber(analytics.clicks)}</span>
                  </div>
                  <div className="adv-metric-card">
                    <span className="adv-metric-label">CTR</span>
                    <span className="adv-metric-value">{formatPercent(analytics.ctr)}</span>
                  </div>
                  <div className="adv-metric-card">
                    <span className="adv-metric-label">Video Views</span>
                    <span className="adv-metric-value">{formatNumber(analytics.video_views)}</span>
                  </div>
                  <div className="adv-metric-card">
                    <span className="adv-metric-label">Completed Views</span>
                    <span className="adv-metric-value">{formatNumber(analytics.completed_views)}</span>
                  </div>
                  <div className="adv-metric-card">
                    <span className="adv-metric-label">Avg Watch Time</span>
                    <span className="adv-metric-value">
                      {analytics.average_watch_time_seconds != null
                        ? `${Number(analytics.average_watch_time_seconds).toFixed(1)}s`
                        : "—"}
                    </span>
                  </div>
                  {analytics.campaign?.budget && (
                    <div className="adv-metric-card">
                      <span className="adv-metric-label">Budget</span>
                      <span className="adv-metric-value">{formatCurrency(analytics.campaign.budget)}</span>
                    </div>
                  )}
                </div>
                {analytics.by_advertisement?.length > 0 && (
                  <div className="adv-by-ad-table">
                    <h4>By Advertisement</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Ad</th>
                          <th>Impressions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.by_advertisement.map((row) => (
                          <tr key={row.advertisement_id}>
                            <td>{row.advertisement__title || `#${row.advertisement_id}`}</td>
                            <td>{formatNumber(row.impressions)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="adv-muted">No analytics data available.</p>
            )}
          </div>
        </Tab>
      </Tabs>

      <Modal show={showAdModal} onHide={() => setShowAdModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingAd ? "Edit Advertisement" : "New Advertisement"}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSaveAd}>
          <Modal.Body>
            {adError && <div className="adv-form-error">{adError}</div>}
            <div className="adv-form-grid">
              <label className="adv-field">
                <span>Title *</span>
                <input
                  className="adv-input"
                  value={adForm.title}
                  onChange={(e) => setAdForm({ ...adForm, title: e.target.value })}
                  required
                />
              </label>
              <label className="adv-field">
                <span>Ad Type *</span>
                <select
                  className="adv-select adv-select--full"
                  value={adForm.ad_type}
                  onChange={(e) => setAdForm({ ...adForm, ad_type: e.target.value })}
                >
                  {AD_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="adv-field adv-field--full">
                <span>Description</span>
                <textarea
                  className="adv-textarea"
                  rows={2}
                  value={adForm.description}
                  onChange={(e) => setAdForm({ ...adForm, description: e.target.value })}
                />
              </label>
              <label className="adv-field">
                <span>CTA Text</span>
                <input
                  className="adv-input"
                  value={adForm.cta_text}
                  onChange={(e) => setAdForm({ ...adForm, cta_text: e.target.value })}
                />
              </label>
              <label className="adv-field">
                <span>Destination URL *</span>
                <input
                  type="url"
                  className="adv-input"
                  value={adForm.destination_url}
                  onChange={(e) => setAdForm({ ...adForm, destination_url: e.target.value })}
                  required
                />
              </label>
              <label className="adv-field adv-field--full">
                <span>Placements (empty = all)</span>
                <Select
                  isMulti
                  options={PLACEMENT_OPTIONS}
                  value={PLACEMENT_OPTIONS.filter((o) => (adForm.placements || []).includes(o.value))}
                  onChange={(sel) => setAdForm({ ...adForm, placements: (sel || []).map((s) => s.value) })}
                  styles={selectStyles}
                />
              </label>
              {adForm.ad_type === "image" && (
                <label className="adv-field adv-field--full">
                  <span>Banner Image {editingAd ? "(optional)" : "*"}</span>
                  {editingAd?.banner_image && !adForm.banner_image ? (
                    <img src={editingAd.banner_image} alt="" className="adv-preview-img" />
                  ) : null}
                  <input
                    type="file"
                    accept="image/*"
                    className="adv-input"
                    onChange={(e) => setAdForm({ ...adForm, banner_image: e.target.files?.[0] || null })}
                  />
                </label>
              )}
              {adForm.ad_type === "video" && (
                <label className="adv-field adv-field--full">
                  <span>Video File {editingAd?.video_url ? "(optional — replace)" : ""}</span>
                  <input
                    type="file"
                    accept="video/*"
                    className="adv-input"
                    onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  />
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="adv-progress">
                      <div className="adv-progress-bar" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  )}
                </label>
              )}
              <label className="adv-field adv-field--checkbox">
                <input
                  type="checkbox"
                  checked={adForm.is_active}
                  onChange={(e) => setAdForm({ ...adForm, is_active: e.target.checked })}
                />
                <span>Active</span>
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={adSaving}>
              {adSaving ? "Saving…" : editingAd ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {alertDeleteType === "success" && (
        <AlertDeletePopupModal
          AlertDeletePopupType={alertDeleteType}
          HandleDelete={confirmDeleteAd}
          CancelAlerrt={() => setAlertDeleteType("")}
          row={deleteAdUuid}
        />
      )}
      {deletePopupType !== "" && (
        <DeletePopupModal
          deletePopupType={deletePopupType}
          setDeletepopupType={setDeletePopupType}
        />
      )}
    </div>
  );
};

export { AdvertisingCampaignDetail };
